import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import CadastroEmailsClienteModal from '../components/CadastroEmailsClienteModal'
import { useEmpresa } from '../contexts/EmpresaContext'
import { useIbsCbsDescricoes } from '../hooks/useIbsCbsDescricoes'

interface CteOsRow {
  id: number
  empcod: number
  serie: string
  numero: number
  chave: string
  cnpj_tomador: string
  nome_tomador: string
  data_emissao: string
  data_saida: string | null
  valor_total: string
  cfop: number | null
  natureza_cod?: number | null
  natureza_det?: string | null
  cstat: string
  xmotivo: string
  protocolo: string
  dh_recbto: string | null
  lote: string
  situacao: string
  finalidade: number
  tp_amb: string
  seq_correcao: number
  cstat_correcao: string | null
  protocolo_correcao: string | null
}

interface Filtros {
  cliente: string
  numero: string
  dataInicio: string
  dataFim: string
  situacao: string
}

// Campos corrigíveis no CT-e OS — label exibido + tags XML correspondentes
const CAMPOS_CCE = [
  { label: 'Observações gerais',                grupo: 'compl',      campo: 'xObs'      },
  { label: 'Características do transporte',     grupo: 'compl',      campo: 'xCaracAd'  },
  { label: 'Características do serviço',        grupo: 'compl',      campo: 'xCaracSer' },
  { label: 'Funcionário emissor',               grupo: 'compl',      campo: 'xEmi'      },
  { label: 'Descrição do serviço',              grupo: 'infServico', campo: 'xDescServ' },
  { label: 'Data / hora da viagem',             grupo: 'infServico', campo: 'dhViagem'  },
  { label: 'Placa do veículo',                  grupo: 'veic',       campo: 'placa'     },
  { label: 'RENAVAM do veículo',                grupo: 'veic',       campo: 'RENAVAM'   },
  { label: 'Outro campo (preencher manualmente)', grupo: '',         campo: ''          },
]

const SITUACAO_LABEL: Record<string, { label: string; color: string }> = {
  LIBERADA_ENVIO: { label: 'Liberada Envio', color: '#b45309' },
  AUTORIZADA:     { label: 'Autorizada',     color: '#15803d' },
  REJEITADA:      { label: 'Rejeitada',       color: '#7e22ce' },
  CANCELADA:      { label: 'Cancelada',       color: '#dc2626' },
}

const SIT_BG: Record<string, string> = {
  LIBERADA_ENVIO: '#fef3c7',
  AUTORIZADA:     '#dcfce7',
  REJEITADA:      '#f3e8ff',
  CANCELADA:      '#fee2e2',
}

function fmtDateInput(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function filtroPadrao(): Filtros {
  const hoje = new Date()
  const ontem = new Date(hoje)
  ontem.setDate(ontem.getDate() - 1)
  return { cliente: '', numero: '', dataInicio: fmtDateInput(ontem), dataFim: fmtDateInput(hoje), situacao: '' }
}

function fmtData(iso: string | null): string {
  if (!iso) return '-'
  return iso.slice(0, 10).split('-').reverse().join('/')
}

function fmtMoeda(v: string | number): string {
  const n = typeof v === 'string' ? parseFloat(v) : v
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtCnpj(doc: string): string {
  const n = doc.replace(/\D/g, '')
  if (n.length === 14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  return doc
}

// SEFAZ às vezes concatena mais de uma falha na mesma mensagem (schema, regra de negócio etc.)
function splitErros(motivo: string): string[] {
  return motivo
    .split(/\s*[;\n]\s*|(?<=\])\s*(?=\[)/g)
    .map(s => s.trim())
    .filter(Boolean)
}

export default function ListaCTeOS() {
  const navigate = useNavigate()
  const { empresas, empresaCod } = useEmpresa()
  const { cstDes, classTribDes } = useIbsCbsDescricoes()
  const [lista, setLista] = useState<CteOsRow[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [transmitindo, setTransmitindo] = useState<number | null>(null)
  const [msg, setMsg] = useState<{ id: number; texto: string; ok: boolean } | null>(null)

  const [filtros, setFiltros] = useState<Filtros>(filtroPadrao)
  const [pesquisaAtiva, setPesquisaAtiva] = useState<Filtros>(filtroPadrao)

  const [cancelModal, setCancelModal] = useState<CteOsRow | null>(null)
  const [xJust, setXJust] = useState('')
  const [cancelando, setCancelando] = useState(false)
  const [erroJust, setErroJust] = useState<string | null>(null)

  const [erroModal, setErroModal] = useState<CteOsRow | null>(null)

  const [consultaRow, setConsultaRow] = useState<CteOsRow | null>(null)
  const [consultaPayload, setConsultaPayload] = useState<any>(null)
  const [consultaFinanceiro, setConsultaFinanceiro] = useState<{ numero: string; vencimento: string; valor: number; saldo: number; contaCod: string }[]>([])
  const [carregandoConsulta, setCarregandoConsulta] = useState(false)

  const [correcaoModal, setCorrecaoModal] = useState<CteOsRow | null>(null)
  const [correcoes, setCorrecoes] = useState([{ grupoAlterado: '', campoAlterado: '', valorAlterado: '', isCustom: false }])
  const [enviandoCce, setEnviandoCce] = useState(false)
  const [erroCce, setErroCce] = useState<string | null>(null)
  const [cceMenuId, setCceMenuId] = useState<number | null>(null)
  const [cceMenuPos, setCceMenuPos] = useState<{ top: number; left: number } | null>(null)

  const [acoesMenuId, setAcoesMenuId] = useState<number | null>(null)
  const [acoesMenuPos, setAcoesMenuPos] = useState<{ top: number; left: number } | null>(null)

  // Financeiro
  const [finRow, setFinRow] = useState<CteOsRow | null>(null)
  const [finExistente, setFinExistente] = useState<{ numero: string; vencimento: string; valor: number; saldo: number; contaCod: string }[]>([])
  const [finCarregando, setFinCarregando] = useState(false)
  const [finRetencao, setFinRetencao] = useState(0)
  const [finQtd, setFinQtd] = useState(1)
  const [finIntervalo, setFinIntervalo] = useState(30)
  const [finParcelas, setFinParcelas] = useState<{ numero: string; vencimento: string; valor: string }[]>([])
  const [finContaCod, setFinContaCod] = useState('')
  const [finContaNome, setFinContaNome] = useState('')
  const [finContaBusca, setFinContaBusca] = useState('')
  const [finContaResultados, setFinContaResultados] = useState<{ dep_cod: string; dep_des: string }[]>([])
  const [finContaAberto, setFinContaAberto] = useState(false)
  const [finSalvando, setFinSalvando] = useState(false)
  const [finDeletando, setFinDeletando] = useState(false)
  const [finErro, setFinErro] = useState<string | null>(null)

  // Envio de e-mail
  const [emailModal, setEmailModal] = useState<CteOsRow | null>(null)
  const [emailCarregando, setEmailCarregando] = useState(false)
  const [emailEnviando, setEmailEnviando] = useState(false)
  const [emailErro, setEmailErro] = useState<string | null>(null)
  const [emailRemetenteNome, setEmailRemetenteNome] = useState('')
  const [emailRemetenteEmail, setEmailRemetenteEmail] = useState('')
  const [emailDestNome, setEmailDestNome] = useState('')
  const [emailDestCnpj, setEmailDestCnpj] = useState('')
  const [emailDestinatario, setEmailDestinatario] = useState('')
  const [emailCopias, setEmailCopias] = useState<string[]>([])
  const [cadEmailsAberto, setCadEmailsAberto] = useState(false)
  const [emailAssunto, setEmailAssunto] = useState('')
  const [emailCorpo, setEmailCorpo] = useState('')

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const params: Record<string, string> = { empcod: String(empresaCod ?? 1) }
      if (pesquisaAtiva.cliente)    params.cliente    = pesquisaAtiva.cliente
      if (pesquisaAtiva.numero)     params.numero     = pesquisaAtiva.numero
      if (pesquisaAtiva.dataInicio) params.dataInicio = pesquisaAtiva.dataInicio
      if (pesquisaAtiva.dataFim)    params.dataFim    = pesquisaAtiva.dataFim
      if (pesquisaAtiva.situacao)   params.situacao   = pesquisaAtiva.situacao
      const r = await api.get('/cte-os', { params })
      setLista(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao carregar lista')
    } finally {
      setCarregando(false)
    }
  }, [pesquisaAtiva, empresaCod])

  useEffect(() => { carregar() }, [carregar])

  function pesquisar() {
    setMsg(null)
    setPesquisaAtiva({ ...filtros })
  }

  function limpar() {
    const padrao = filtroPadrao()
    setFiltros(padrao)
    setPesquisaAtiva(padrao)
    setMsg(null)
  }

  async function transmitir(row: CteOsRow) {
    setTransmitindo(row.id)
    setMsg(null)
    try {
      const r = await api.post(`/cte-os/${row.id}/transmitir`)
      const res = r.data
      const ok = res.autorizado === true
      const texto = ok
        ? `Autorizado — Protocolo ${res.protocolo}`
        : res.cStat
          ? `cStat ${res.cStat}: ${res.xMotivo}`
          : res.erro || 'Sem resposta do SEFAZ'
      setMsg({ id: row.id, texto, ok })
      await carregar()
    } catch (e: any) {
      setMsg({ id: row.id, texto: e?.response?.data?.error || 'Erro ao transmitir', ok: false })
    } finally {
      setTransmitindo(null)
    }
  }

  function baixarXml(row: CteOsRow)   { window.open(`/api/cte-os/${row.id}/xml`,   '_blank') }
  function baixarDacte(row: CteOsRow) { window.open(`/api/cte-os/${row.id}/dacte`, '_blank') }

  function toggleMenuAcoes(e: React.MouseEvent, id: number) {
    if (acoesMenuId === id) { setAcoesMenuId(null); setAcoesMenuPos(null); return }
    const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect()
    setAcoesMenuId(id)
    setAcoesMenuPos({ top: rect.bottom + 2, left: rect.left })
  }
  function fecharMenuAcoes() { setAcoesMenuId(null); setAcoesMenuPos(null) }

  const menuItemStyle: React.CSSProperties = {
    display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px',
    fontSize: '0.78rem', color: '#1f2937', background: 'none', border: 'none',
    cursor: 'pointer', whiteSpace: 'nowrap',
  }
  const onMenuHover = (e: React.MouseEvent<HTMLElement>) => (e.currentTarget.style.background = '#f3f4f6')
  const onMenuLeave = (e: React.MouseEvent<HTMLElement>) => (e.currentTarget.style.background = '')

  async function abrirEmail(row: CteOsRow) {
    setEmailModal(row); setEmailErro(null); setEmailCarregando(true)
    setEmailRemetenteNome(''); setEmailRemetenteEmail('')
    setEmailDestNome(''); setEmailDestCnpj(''); setEmailDestinatario('')
    setEmailCopias([]); setEmailAssunto(''); setEmailCorpo('')
    try {
      const r = await api.get(`/cte-os/${row.id}/email-preview`)
      const d = r.data
      setEmailRemetenteNome(d.remetenteNome ?? '')
      setEmailRemetenteEmail(d.remetenteEmail ?? '')
      setEmailDestNome(d.destinatarioNome ?? '')
      setEmailDestCnpj(d.destinatarioCnpj ?? '')
      setEmailDestinatario(d.destinatarioEmail ?? '')
      setEmailCopias(d.copias ?? [])
      setEmailAssunto(d.assunto ?? '')
      setEmailCorpo(d.corpo ?? '')
    } catch (e: any) {
      setEmailErro(e?.response?.data?.error || 'Erro ao carregar dados de e-mail')
    } finally {
      setEmailCarregando(false)
    }
  }

  function fecharEmail() { if (emailEnviando) return; setEmailModal(null) }

  function removerCopiaEmail(idx: number) {
    setEmailCopias(cs => cs.filter((_, i) => i !== idx))
  }

  async function enviarEmailNota() {
    if (!emailModal) return
    if (!emailDestinatario.trim()) { setEmailErro('Informe o e-mail do destinatário'); return }
    setEmailEnviando(true); setEmailErro(null)
    try {
      await api.post(`/cte-os/${emailModal.id}/enviar-email`, {
        destinatario: emailDestinatario.trim(),
        copias: emailCopias.map(c => c.trim()).filter(Boolean),
        assunto: emailAssunto,
        corpo: emailCorpo,
      })
      setMsg({ id: emailModal.id, texto: 'E-mail enviado com sucesso!', ok: true })
      setEmailModal(null)
    } catch (e: any) {
      setEmailErro(e?.response?.data?.error || 'Erro ao enviar e-mail')
    } finally {
      setEmailEnviando(false)
    }
  }

  function abrirCancelar(row: CteOsRow) {
    setCancelModal(row)
    setXJust('')
    setErroJust(null)
  }

  function fecharCancelar() {
    if (cancelando) return
    setCancelModal(null)
    setXJust('')
    setErroJust(null)
  }

  async function confirmarCancelamento() {
    if (!cancelModal) return
    if (xJust.trim().length < 15) {
      setErroJust('Justificativa deve ter no mínimo 15 caracteres')
      return
    }
    setCancelando(true)
    setErroJust(null)
    try {
      const r = await api.post(`/cte-os/${cancelModal.id}/cancelar`, { xJust: xJust.trim() })
      const res = r.data
      const ok = res.cancelado === true
      const texto = ok
        ? `CT-e OS cancelada — Protocolo ${res.protocolo ?? ''}`
        : res.cStat
          ? `cStat ${res.cStat}: ${res.xMotivo}`
          : res.erro || 'Sem resposta do SEFAZ'
      setMsg({ id: cancelModal.id, texto, ok })
      setCancelModal(null)
      await carregar()
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Erro ao cancelar'
      setErroJust(msg)
    } finally {
      setCancelando(false)
    }
  }

  const novaCorrecao = () => ({ grupoAlterado: '', campoAlterado: '', valorAlterado: '', isCustom: false })

  function abrirCorrecao(row: CteOsRow) {
    setCorrecaoModal(row)
    setCorrecoes([novaCorrecao()])
    setErroCce(null)
  }

  function fecharCorrecao() {
    if (enviandoCce) return
    setCorrecaoModal(null)
    setCorrecoes([novaCorrecao()])
    setErroCce(null)
  }

  function selecionarCampo(idx: number, key: string) {
    if (key === 'outro') {
      setCorrecoes(prev => prev.map((c, i) => i === idx ? { ...c, grupoAlterado: '', campoAlterado: '', isCustom: true } : c))
    } else {
      const opt = CAMPOS_CCE.find(f => `${f.grupo}|${f.campo}` === key)
      if (opt) setCorrecoes(prev => prev.map((c, i) => i === idx ? { ...c, grupoAlterado: opt.grupo, campoAlterado: opt.campo, isCustom: false } : c))
    }
    setErroCce(null)
  }

  function atualizarCorrecao(idx: number, campo: string, valor: string) {
    setCorrecoes(prev => prev.map((c, i) => i === idx ? { ...c, [campo]: valor } : c))
    setErroCce(null)
  }

  function adicionarCorrecao() {
    if (correcoes.length >= 10) return
    setCorrecoes(prev => [...prev, novaCorrecao()])
  }

  function removerCorrecao(idx: number) {
    if (correcoes.length <= 1) return
    setCorrecoes(prev => prev.filter((_, i) => i !== idx))
  }

  async function confirmarCorrecao() {
    if (!correcaoModal) return
    const validas = correcoes.filter(c => c.grupoAlterado.trim() && c.campoAlterado.trim() && c.valorAlterado.trim())
    if (validas.length === 0) {
      setErroCce('Preencha ao menos uma correção com grupo, campo e valor')
      return
    }
    setEnviandoCce(true)
    setErroCce(null)
    try {
      const r = await api.post(`/cte-os/${correcaoModal.id}/corrigir`, { correcoes: validas })
      const res = r.data
      const ok = res.registrado === true
      const texto = ok
        ? `CC-e registrada — Seq ${res.nSeqEvento} — Protocolo ${res.protocolo ?? ''}`
        : res.cStat
          ? `cStat ${res.cStat}: ${res.xMotivo}`
          : res.erro || 'Sem resposta do SEFAZ'
      setMsg({ id: correcaoModal.id, texto, ok })
      setCorrecaoModal(null)
    } catch (e: any) {
      setErroCce(e?.response?.data?.error || 'Erro ao enviar Carta de Correção')
    } finally {
      setEnviandoCce(false)
    }
  }

  async function abrirConsulta(row: CteOsRow) {
    setConsultaRow(row)
    setConsultaPayload(null)
    setConsultaFinanceiro([])
    setCarregandoConsulta(true)
    try {
      if (row.id) {
        const [payloadR, finR] = await Promise.all([
          api.get(`/cte-os/${row.id}/payload`).catch(() => null),
          api.get(`/cte-os/${row.id}/financeiro`).catch(() => null),
        ])
        setConsultaPayload(payloadR?.data ?? null)
        setConsultaFinanceiro(finR?.data?.parcelas ?? [])
      } else {
        // Nota emitida direto pelo Frota — sem cte_os_doc/payload, reconstrói de ntvits/ntvcpo
        const r = await api.get(`/cte-os/erp-detalhe/${row.chave}`).catch(() => null)
        setConsultaPayload(r?.data ?? null)
        setConsultaFinanceiro(r?.data?.financeiro ?? [])
      }
    } finally {
      setCarregandoConsulta(false)
    }
  }

  function fecharConsulta() {
    setConsultaRow(null)
    setConsultaPayload(null)
    setConsultaFinanceiro([])
  }

  async function abrirFinanceiro(row: CteOsRow) {
    setFinRow(row)
    setFinExistente([])
    setFinParcelas([])
    setFinContaCod('')
    setFinContaNome('')
    setFinContaBusca('')
    setFinErro(null)
    setFinQtd(1)
    setFinIntervalo(30)
    setFinCarregando(true)
    try {
      const r = await api.get(`/cte-os/${row.id}/financeiro`)
      setFinExistente(r.data.parcelas ?? [])
      setFinRetencao(Number(r.data.retencaoTotal ?? 0))
    } catch { /* sem financeiro ainda */ }
    finally { setFinCarregando(false) }
  }

  function fecharFinanceiro() {
    if (finSalvando || finDeletando) return
    setFinRow(null)
    setFinExistente([])
    setFinParcelas([])
    setFinContaCod('')
    setFinContaNome('')
    setFinContaBusca('')
    setFinErro(null)
    setFinContaResultados([])
    setFinContaAberto(false)
  }

  function recalcularParcelas(qtd: number, intervalo: number, row: CteOsRow) {
    const base = new Date(row.data_emissao)
    const total = parseFloat(row.valor_total)
    setFinParcelas(Array.from({ length: qtd }, (_, i) => {
      const d = new Date(base)
      d.setDate(d.getDate() + intervalo * (i + 1))
      return { numero: String(i + 1).padStart(2, '0'), vencimento: d.toISOString().slice(0, 10), valor: (total / qtd).toFixed(2) }
    }))
  }

  async function buscarConta(cod: string) {
    if (!cod.trim()) return
    try {
      const r = await api.get('/erp/contas', { params: { busca: cod.trim() } })
      if (r.data.length === 1) {
        setFinContaCod(r.data[0].dep_cod.trim())
        setFinContaNome(r.data[0].dep_des)
        setFinContaResultados([])
        setFinContaAberto(false)
      } else if (r.data.length > 1) {
        setFinContaResultados(r.data)
        setFinContaAberto(true)
      }
    } catch { /* silencioso */ }
  }

  async function salvarFinanceiroLista() {
    if (!finRow) return
    if (finParcelas.length === 0) { setFinErro('Calcule as parcelas primeiro'); return }
    if (!finContaCod.trim()) { setFinErro('Informe a conta financeira'); return }
    setFinSalvando(true)
    setFinErro(null)
    try {
      await api.post(`/cte-os/${finRow.id}/financeiro`, {
        parcelas: finParcelas,
        contaCod: finContaCod,
        retencaoTotal: finRetencao,
      })
      const r = await api.get(`/cte-os/${finRow.id}/financeiro`)
      setFinExistente(r.data.parcelas ?? [])
      setFinParcelas([])
      setMsg({ id: finRow.id, texto: 'Financeiro salvo com sucesso!', ok: true })
    } catch (e: any) {
      setFinErro(e?.response?.data?.error || 'Erro ao salvar financeiro')
    } finally { setFinSalvando(false) }
  }

  async function deletarFinanceiroLista() {
    if (!finRow) return
    setFinDeletando(true)
    setFinErro(null)
    try {
      await api.delete(`/cte-os/${finRow.id}/financeiro`)
      setFinExistente([])
      setMsg({ id: finRow.id, texto: 'Financeiro excluído.', ok: true })
    } catch (e: any) {
      setFinErro(e?.response?.data?.error || 'Erro ao excluir financeiro')
    } finally { setFinDeletando(false) }
  }

  const secTitle = (title: string) => (
    <div style={{
      background: '#f3f4f6', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb',
      padding: '4px 1.25rem', fontSize: '0.68rem', fontWeight: 700,
      color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em',
    }}>{title}</div>
  )

  const kv = (label: string, value: any) => (
    <div>
      <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' as const, marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: '0.82rem', color: '#374151' }}>{value ?? '—'}</div>
    </div>
  )

  // Nº da Viagem tem destaque próprio (mesmo tom âmbar da emissão) pra chamar
  // atenção de que a nota está vinculada a uma ficha do módulo Turismo
  const kvViagem = (value: any) => (
    <div>
      <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#b45309', textTransform: 'uppercase' as const, marginBottom: 1 }}>Nº da Viagem</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400e' }}>{value ?? '—'}</div>
    </div>
  )

  const inp: React.CSSProperties = {
    border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
    fontSize: '0.82rem', height: 30, background: '#fff',
  }

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, flex: 1, margin: 0 }}>
          CT-e OS — Transmissão e Consulta
        </h2>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/cte/emitir')}>
          + Nova CT-e OS
        </button>
      </div>

      {/* Filtros */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end',
        background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6,
        padding: '0.6rem 0.75rem', marginBottom: '0.75rem',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <label style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600 }}>CLIENTE</label>
          <input
            style={{ ...inp, width: 200 }}
            placeholder="Nome do tomador"
            value={filtros.cliente}
            onChange={e => setFiltros(f => ({ ...f, cliente: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <label style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600 }}>Nº NOTA</label>
          <input
            style={{ ...inp, width: 90 }}
            type="number"
            placeholder="Número"
            value={filtros.numero}
            onChange={e => setFiltros(f => ({ ...f, numero: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <label style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600 }}>PERÍODO DE</label>
          <input
            style={{ ...inp, width: 130 }}
            type="date"
            value={filtros.dataInicio}
            onChange={e => setFiltros(f => ({ ...f, dataInicio: e.target.value }))}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <label style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600 }}>ATÉ</label>
          <input
            style={{ ...inp, width: 130 }}
            type="date"
            value={filtros.dataFim}
            onChange={e => setFiltros(f => ({ ...f, dataFim: e.target.value }))}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <label style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600 }}>SITUAÇÃO</label>
          <select
            style={{ ...inp, width: 140 }}
            value={filtros.situacao}
            onChange={e => setFiltros(f => ({ ...f, situacao: e.target.value }))}
          >
            <option value="">Todas</option>
            <option value="LIBERADA_ENVIO">Liberada Envio</option>
            <option value="AUTORIZADA">Autorizada</option>
            <option value="REJEITADA">Rejeitada</option>
            <option value="CANCELADA">Cancelada</option>
          </select>
        </div>
        <button className="btn btn-primary btn-sm" onClick={pesquisar} disabled={carregando}
          style={{ height: 30, alignSelf: 'flex-end' }}>
          {carregando ? 'Buscando...' : 'Pesquisar'}
        </button>
        <button className="btn btn-sm" onClick={limpar}
          style={{ height: 30, alignSelf: 'flex-end', color: '#6b7280' }}>
          Limpar
        </button>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}
      {msg  && (
        <div className={`alert ${msg.ok ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '0.75rem' }}>
          {msg.texto}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="table" style={{ fontSize: '0.82rem', minWidth: 960, borderCollapse: 'collapse' }}>
          <thead>
            <tr className="thead-claro" style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ width: 40, textAlign: 'center' }}>Sér</th>
              <th style={{ width: 70 }}>Número</th>
              <th style={{ width: 155, whiteSpace: 'nowrap' }}>CNPJ Tomador</th>
              <th>Tomador</th>
              <th style={{ width: 90 }}>Emissão</th>
              <th style={{ width: 100, textAlign: 'right' }}>Val. Total</th>
              <th style={{ width: 50, textAlign: 'center' }}>cStat</th>
              <th style={{ width: 120 }}>Situação</th>
              <th style={{ width: 130 }}>Protocolo</th>
              <th style={{ width: 130 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {carregando && (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Carregando...</td></tr>
            )}
            {!carregando && lista.length === 0 && (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhuma CT-e OS encontrada</td></tr>
            )}
            {lista.map(row => {
              const sit = SITUACAO_LABEL[row.situacao] ?? { label: row.situacao, color: '#374151' }
              const isTransmitindo = transmitindo === row.id
              return (
                <tr key={row.chave} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ textAlign: 'center', color: '#6b7280' }}>{row.serie}</td>
                  <td style={{ fontWeight: 700 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      {(row.situacao === 'AUTORIZADA' || row.situacao === 'CANCELADA' || row.situacao === 'REJEITADA') ? (
                        <span
                          onClick={() => abrirConsulta(row)}
                          style={{ cursor: 'pointer', color: row.situacao === 'REJEITADA' ? '#dc2626' : '#1d4ed8', textDecoration: 'underline' }}
                          title="Clique para ver os dados da nota"
                        >
                          {row.numero}
                        </span>
                      ) : row.numero}
                      {row.seq_correcao > 0 && (
                        <div style={{ display: 'inline-block' }}>
                          <button
                            onClick={e => {
                              if (cceMenuId === row.id) {
                                setCceMenuId(null); setCceMenuPos(null);
                              } else {
                                const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                                setCceMenuId(row.id);
                                setCceMenuPos({ top: rect.bottom + 2, left: rect.left });
                              }
                            }}
                            title={`CC-e registrada — Seq ${row.seq_correcao}`}
                            style={{
                              fontSize: '0.6rem', fontWeight: 700, padding: '1px 5px',
                              borderRadius: 4, background: '#fef3c7', color: '#92400e',
                              border: '1px solid #fde68a', whiteSpace: 'nowrap',
                              cursor: 'pointer',
                            }}
                          >
                            CC-e
                          </button>
                          {cceMenuId === row.id && cceMenuPos && (
                            <>
                              <div
                                onClick={() => { setCceMenuId(null); setCceMenuPos(null); }}
                                style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                              />
                              <div style={{
                                position: 'fixed', top: cceMenuPos.top, left: cceMenuPos.left,
                                zIndex: 100, background: '#fff', border: '1px solid #e5e7eb',
                                borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                minWidth: 160, overflow: 'hidden',
                              }}>
                                <a
                                  href={`${api.defaults.baseURL}/cte-os/${row.id}/dacce`}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={() => { setCceMenuId(null); setCceMenuPos(null); }}
                                  style={{ display: 'block', padding: '7px 12px', fontSize: '0.78rem', color: '#1f2937', textDecoration: 'none' }}
                                  onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
                                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                                >
                                  Baixar PDF (DACCE)
                                </a>
                                <a
                                  href={`${api.defaults.baseURL}/cte-os/${row.id}/cce-xml`}
                                  download={`CCe_${row.chave}.xml`}
                                  onClick={() => { setCceMenuId(null); setCceMenuPos(null); }}
                                  style={{ display: 'block', padding: '7px 12px', fontSize: '0.78rem', color: '#1f2937', textDecoration: 'none', borderTop: '1px solid #f3f4f6' }}
                                  onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
                                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                                >
                                  Baixar XML
                                </a>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {fmtCnpj(row.cnpj_tomador)}
                  </td>
                  <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.nome_tomador}>
                    {row.nome_tomador}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtData(row.data_emissao)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {fmtMoeda(row.valor_total)}
                  </td>
                  <td
                    style={{
                      textAlign: 'center', fontFamily: 'monospace',
                      color: row.situacao === 'REJEITADA' ? '#dc2626' : '#6b7280',
                      cursor: row.situacao === 'REJEITADA' ? 'pointer' : 'default',
                      textDecoration: row.situacao === 'REJEITADA' ? 'underline' : 'none',
                    }}
                    onClick={() => { if (row.situacao === 'REJEITADA') setErroModal(row) }}
                    title={row.situacao === 'REJEITADA' ? 'Clique para ver o motivo da rejeição' : undefined}
                  >
                    {row.cstat || '-'}
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                      fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap',
                      background: SIT_BG[row.situacao] ?? '#f3f4f6',
                      color: sit.color,
                    }}>
                      {sit.label}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.76rem', color: '#6b7280' }}>
                    {row.protocolo || '-'}
                  </td>
                  <td>
                    {!row.id ? (
                      <span style={{ color: '#9ca3af', fontSize: '0.72rem', fontStyle: 'italic' }} title="Nota emitida direto pelo Frota — sem ações disponíveis aqui">
                        Emitida no Frota
                      </span>
                    ) : (
                    <>
                    {(row.situacao === 'LIBERADA_ENVIO' || row.situacao === 'REJEITADA') && (
                      <button className="btn btn-orange btn-sm"
                        disabled={isTransmitindo}
                        onClick={() => transmitir(row)}
                        title="Transmitir ao SEFAZ"
                        style={{ whiteSpace: 'nowrap', marginRight: '0.25rem' }}>
                        {isTransmitindo ? '...' : 'Transmitir'}
                      </button>
                    )}
                    <button className="btn btn-sm"
                      onClick={e => toggleMenuAcoes(e, row.id)}
                      style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      Ações ▾
                    </button>
                    {acoesMenuId === row.id && acoesMenuPos && (
                      <>
                        <div onClick={fecharMenuAcoes} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
                        <div style={{
                          position: 'fixed', top: acoesMenuPos.top, left: acoesMenuPos.left,
                          zIndex: 100, background: '#fff', border: '1px solid #e5e7eb',
                          borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                          minWidth: 190, overflow: 'hidden', padding: '4px 0',
                        }}>
                          {(row.situacao === 'LIBERADA_ENVIO' || row.situacao === 'REJEITADA') && (
                            <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                              onClick={() => { fecharMenuAcoes(); navigate(`/cte/emitir?editId=${row.id}`) }}>
                              Editar
                            </button>
                          )}
                          {row.situacao === 'AUTORIZADA' && (
                            <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                              onClick={() => { fecharMenuAcoes(); abrirCorrecao(row) }}>
                              Carta de Correção (CC-e)
                            </button>
                          )}
                          <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                            onClick={() => { fecharMenuAcoes(); abrirEmail(row) }}>
                            Enviar por e-mail
                          </button>
                          <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                            onClick={() => { fecharMenuAcoes(); baixarXml(row) }}>
                            Baixar XML
                          </button>
                          <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                            onClick={() => { fecharMenuAcoes(); baixarDacte(row) }}>
                            Baixar DACTE
                          </button>
                          <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                            onClick={() => { fecharMenuAcoes(); navigate(`/cte/emitir?copyId=${row.id}`) }}>
                            Copiar (nova nota)
                          </button>
                          <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                            onClick={() => { fecharMenuAcoes(); abrirFinanceiro(row) }}>
                            Financeiro
                          </button>
                          {row.situacao === 'AUTORIZADA' && (
                            <button style={{ ...menuItemStyle, color: '#dc2626', borderTop: '1px solid #f3f4f6' }} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                              onClick={() => { fecharMenuAcoes(); abrirCancelar(row) }}>
                              Cancelar CT-e OS
                            </button>
                          )}
                        </div>
                      </>
                    )}
                    </>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Modal de envio de e-mail */}
      {emailModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={fecharEmail}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700, color: '#1d4ed8' }}>
              Enviar XML/DACTE por E-mail
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#6b7280' }}>
              Nº {emailModal.numero} — {emailModal.nome_tomador}
            </p>

            {emailCarregando ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>Carregando...</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 1rem', marginBottom: '0.75rem' }}>
                  {kv('Remetente', emailRemetenteNome || '—')}
                  {kv('E-mail Remetente', emailRemetenteEmail || '—')}
                </div>

                {kv('Destinatário', `${emailDestNome}${emailDestCnpj ? ' — ' + fmtCnpj(emailDestCnpj) : ''}`)}

                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', margin: '0.6rem 0 4px' }}>
                  E-mail do Destinatário
                </label>
                <input value={emailDestinatario} onChange={e => setEmailDestinatario(e.target.value)}
                  placeholder="email@exemplo.com"
                  style={{
                    width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db',
                    borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem',
                  }} />

                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', margin: '0.75rem 0 4px' }}>
                  Cópias
                </label>
                {emailCopias.length === 0 && (
                  <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginBottom: 4 }}>Nenhuma cópia cadastrada</div>
                )}
                {emailCopias.map((c, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                    <input value={c}
                      onChange={e => setEmailCopias(cs => cs.map((v, i) => i === idx ? e.target.value : v))}
                      style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: '0.82rem' }} />
                    <button onClick={() => removerCopiaEmail(idx)}
                      title="Remover desta cópia (não altera o cadastro do cliente)"
                      style={{
                        background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5',
                        borderRadius: 4, padding: '0 10px', cursor: 'pointer',
                      }}>
                      ✕
                    </button>
                  </div>
                ))}

                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', margin: '0.75rem 0 4px' }}>
                  Assunto
                </label>
                <input value={emailAssunto} onChange={e => setEmailAssunto(e.target.value)}
                  style={{
                    width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db',
                    borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem',
                  }} />

                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', margin: '0.75rem 0 4px' }}>
                  Mensagem
                </label>
                <textarea value={emailCorpo} onChange={e => setEmailCorpo(e.target.value)} rows={5}
                  style={{
                    width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db',
                    borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem', resize: 'vertical',
                  }} />

                {emailErro && <div style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: 8 }}>{emailErro}</div>}

                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button className="btn btn-sm" onClick={() => setCadEmailsAberto(true)} disabled={emailEnviando || !emailDestCnpj}
                    title="Cadastrar/alterar os e-mails deste cliente sem sair da tela"
                    style={{ marginRight: 'auto', color: '#1b3f7b', border: '1px solid #c7d6ea', background: '#eef3f9', fontWeight: 600 }}>
                    ✉ Cadastrar e-mails
                  </button>
                  {cadEmailsAberto && (
                    <CadastroEmailsClienteModal doc={emailDestCnpj} nome={emailDestNome} tipo="O"
                      onClose={() => setCadEmailsAberto(false)}
                      onSalvo={(principal, copias) => { setEmailDestinatario(principal); setEmailCopias(copias); setCadEmailsAberto(false) }} />
                  )}
                  <button className="btn btn-sm" onClick={fecharEmail} disabled={emailEnviando} style={{ color: '#6b7280' }}>
                    Voltar
                  </button>
                  <button onClick={enviarEmailNota}
                    disabled={emailEnviando || !emailDestinatario.trim()}
                    style={{
                      background: '#1d4ed8', color: '#fff', border: 'none',
                      borderRadius: 4, padding: '5px 16px', fontSize: '0.82rem',
                      fontWeight: 600, cursor: emailEnviando ? 'not-allowed' : 'pointer',
                      opacity: emailEnviando || !emailDestinatario.trim() ? 0.6 : 1,
                    }}>
                    {emailEnviando ? 'Enviando...' : 'Enviar E-mail'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de erro/rejeição — só o motivo, sem resumo da nota */}
      {erroModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setErroModal(null)}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700, color: '#dc2626' }}>
              Rejeitada — Nº {erroModal.numero}
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.78rem', color: '#6b7280', fontFamily: 'monospace' }}>
              cStat {erroModal.cstat || '—'}
            </p>
            {erroModal.xmotivo ? (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {splitErros(erroModal.xmotivo).map((erro, i) => (
                  <li key={i} style={{
                    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
                    padding: '0.55rem 0.75rem', fontSize: '0.82rem', color: '#7f1d1d', lineHeight: 1.4,
                  }}>
                    {erro}
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Sem motivo registrado.</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-sm" onClick={() => setErroModal(null)} style={{ color: '#6b7280' }}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de cancelamento */}
      {cancelModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={fecharCancelar}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700, color: '#dc2626' }}>
              Cancelar CT-e OS
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#6b7280' }}>
              Nº {cancelModal.numero} — {cancelModal.nome_tomador}
            </p>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              Justificativa (mín. 15 caracteres)
            </label>
            <textarea
              autoFocus
              value={xJust}
              onChange={e => { setXJust(e.target.value); setErroJust(null) }}
              rows={4}
              maxLength={255}
              placeholder="Informe o motivo do cancelamento..."
              style={{
                width: '100%', boxSizing: 'border-box',
                border: `1px solid ${erroJust ? '#fca5a5' : '#d1d5db'}`,
                borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem', resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
              <span style={{ fontSize: '0.75rem', color: erroJust ? '#dc2626' : '#9ca3af' }}>
                {erroJust ?? `${xJust.length}/255`}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-sm" onClick={fecharCancelar} disabled={cancelando}
                style={{ color: '#6b7280' }}>
                Voltar
              </button>
              <button
                onClick={confirmarCancelamento}
                disabled={cancelando || xJust.trim().length < 15}
                style={{
                  background: '#dc2626', color: '#fff', border: 'none',
                  borderRadius: 4, padding: '5px 16px', fontSize: '0.82rem',
                  fontWeight: 600, cursor: cancelando ? 'not-allowed' : 'pointer',
                  opacity: cancelando || xJust.trim().length < 15 ? 0.6 : 1,
                }}>
                {cancelando ? 'Cancelando...' : 'Confirmar Cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal de Carta de Correção */}
      {correcaoModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1000, padding: '20px 12px',
        }} onClick={fecharCorrecao}>
          <div style={{
            background: '#fff', borderRadius: 8,
            width: 680, maxWidth: '96vw', maxHeight: 'calc(100vh - 40px)', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>

            {/* Cabeçalho */}
            <div style={{ background: '#fef3c7', borderBottom: '1px solid #fde68a', padding: '0.9rem 1.25rem' }}>
              <h3 style={{ margin: '0 0 0.15rem', fontSize: '1rem', fontWeight: 700, color: '#92400e' }}>
                Carta de Correção Eletrônica (CC-e)
              </h3>
              <div style={{ fontSize: '0.82rem', color: '#92400e' }}>
                Nº {correcaoModal.numero} — {correcaoModal.nome_tomador}
              </div>
              <div style={{ fontSize: '0.74rem', color: '#b45309', marginTop: 4 }}>
                Permitido corrigir apenas dados que não afetam valor do imposto, remetente/destinatário ou data de emissão.
              </div>
            </div>

            {/* Corpo */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem' }}>

              {correcoes.map((c, idx) => {
                const selectedKey = c.isCustom ? 'outro' : (c.grupoAlterado && c.campoAlterado ? `${c.grupoAlterado}|${c.campoAlterado}` : '')
                return (
                  <div key={idx} style={{
                    border: '1px solid #e5e7eb', borderRadius: 6,
                    padding: '0.65rem 0.75rem', marginBottom: '0.5rem',
                    background: '#fff',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem' }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>
                        Correção {idx + 1}
                      </span>
                      {correcoes.length > 1 && (
                        <button onClick={() => removerCorrecao(idx)} style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#ef4444', fontSize: '0.75rem', padding: '0 4px',
                        }}>✕ remover</button>
                      )}
                    </div>

                    {/* Select de campo */}
                    <div style={{ marginBottom: '0.45rem' }}>
                      <label style={{ fontSize: '0.73rem', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 2 }}>Campo a corrigir *</label>
                      <select
                        autoFocus={idx === 0}
                        value={selectedKey}
                        onChange={e => selecionarCampo(idx, e.target.value)}
                        style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: '0.85rem', background: '#fff' }}
                      >
                        <option value="">Selecione o campo...</option>
                        {CAMPOS_CCE.map(f => {
                          const k = f.grupo ? `${f.grupo}|${f.campo}` : 'outro'
                          return <option key={k} value={k}>{f.label}</option>
                        })}
                      </select>
                    </div>

                    {/* Campos manuais (apenas quando "Outro") */}
                    {c.isCustom && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', marginBottom: '0.45rem' }}>
                        <div>
                          <label style={{ fontSize: '0.73rem', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 2 }}>Grupo (tag-pai no XML)</label>
                          <input
                            value={c.grupoAlterado}
                            onChange={e => atualizarCorrecao(idx, 'grupoAlterado', e.target.value)}
                            maxLength={20}
                            placeholder="ex: compl"
                            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 7px', fontSize: '0.83rem' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.73rem', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 2 }}>Campo (nome da tag)</label>
                          <input
                            value={c.campoAlterado}
                            onChange={e => atualizarCorrecao(idx, 'campoAlterado', e.target.value)}
                            maxLength={20}
                            placeholder="ex: xObs"
                            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 7px', fontSize: '0.83rem' }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Novo valor */}
                    <div>
                      <label style={{ fontSize: '0.73rem', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 2 }}>Novo valor *</label>
                      <textarea
                        value={c.valorAlterado}
                        onChange={e => atualizarCorrecao(idx, 'valorAlterado', e.target.value)}
                        rows={2}
                        maxLength={500}
                        placeholder="Digite o valor correto..."
                        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 7px', fontSize: '0.83rem', resize: 'vertical' }}
                      />
                      <div style={{ fontSize: '0.7rem', color: '#9ca3af', textAlign: 'right' }}>{c.valorAlterado.length}/500</div>
                    </div>
                  </div>
                )
              })}

              {correcoes.length < 10 && (
                <button onClick={adicionarCorrecao} style={{
                  background: 'none', border: '1px dashed #d1d5db', borderRadius: 6,
                  width: '100%', padding: '0.4rem', fontSize: '0.8rem',
                  color: '#6b7280', cursor: 'pointer',
                }}>
                  + Adicionar outra correção
                </button>
              )}

              {erroCce && (
                <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#dc2626', background: '#fee2e2', borderRadius: 4, padding: '0.4rem 0.75rem' }}>
                  {erroCce}
                </div>
              )}
            </div>

            {/* Rodapé */}
            <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.65rem 1.25rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', background: '#f9fafb' }}>
              <button className="btn btn-sm" onClick={fecharCorrecao} disabled={enviandoCce} style={{ color: '#6b7280' }}>
                Voltar
              </button>
              <button
                onClick={confirmarCorrecao}
                disabled={enviandoCce}
                style={{
                  background: '#d97706', color: '#fff', border: 'none',
                  borderRadius: 4, padding: '5px 18px', fontSize: '0.82rem',
                  fontWeight: 600, cursor: enviandoCce ? 'not-allowed' : 'pointer',
                  opacity: enviandoCce ? 0.6 : 1,
                }}>
                {enviandoCce ? 'Enviando...' : 'Enviar CC-e'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de consulta completo */}
      {consultaRow && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1000, padding: '16px 10px',
        }} onClick={fecharConsulta}>
          <div style={{
            background: '#fff', borderRadius: 8,
            width: 920, maxWidth: '97vw', maxHeight: 'calc(100vh - 32px)', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
          }} onClick={e => e.stopPropagation()}>

            {/* Cabeçalho azul */}
            <div style={{
              background: '#1a6ea8', color: '#fff', flexShrink: 0,
              padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.02rem', letterSpacing: 0.2 }}>
                  CT-e OS Nº {String(consultaRow.numero).padStart(9, '0')} — Série {consultaRow.serie?.trim()}
                </div>
                <div style={{ fontSize: '0.78rem', opacity: 0.8, marginTop: 3 }}>
                  Emissão: {fmtData(consultaRow.data_emissao)}
                  {consultaRow.tp_amb === '2' && (
                    <span style={{ marginLeft: 10, background: '#f59e0b', color: '#1a6ea8', padding: '1px 7px', borderRadius: 4, fontWeight: 700, fontSize: '0.7rem' }}>HOMOLOGAÇÃO</span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                <span style={{
                  padding: '3px 10px', borderRadius: 12, fontSize: '0.74rem', fontWeight: 700,
                  background: SIT_BG[consultaRow.situacao] ?? '#f3f4f6',
                  color: (SITUACAO_LABEL[consultaRow.situacao] ?? { color: '#374151' }).color,
                }}>
                  {(SITUACAO_LABEL[consultaRow.situacao] ?? { label: consultaRow.situacao }).label}
                </span>
                <button onClick={fecharConsulta} style={{
                  background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer',
                  color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '1rem', lineHeight: 1,
                }}>✕</button>
              </div>
            </div>

            {/* Corpo scrollável */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {carregandoConsulta ? (
                <div style={{ textAlign: 'center', padding: '4rem', color: '#9ca3af' }}>Carregando dados da nota...</div>
              ) : (
                <>
                  {/* ── IDENTIFICAÇÃO ── */}
                  {secTitle('Identificação')}
                  <div style={{ padding: '0.7rem 1.25rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.45rem 1.5rem', marginBottom: '0.45rem' }}>
                      {kv('Empresa', consultaRow.empcod
                        ? (() => { const e = empresas.find(x => x.empcod === consultaRow.empcod); return e ? `${e.empcod} - ${e.empfan || e.empnom}` : `Cód. ${consultaRow.empcod}` })()
                        : '—')}
                      {kv('Série / Número', `${consultaRow.serie?.trim() || '—'} / ${consultaRow.numero}`)}
                      {kv('Data de Emissão', fmtData(consultaRow.data_emissao))}
                      {kv('CFOP', consultaRow.cfop ?? consultaPayload?.naturezaCod ?? '—')}
                      {kv('Modal', consultaPayload?.modal === '2' ? '2 – Intermunicipal' : consultaPayload?.modal === '3' ? '3 – Interestadual' : consultaPayload?.modal === '4' ? '4 – Internacional' : (consultaPayload?.modal ?? '—'))}
                      {kv('Finalidade', consultaPayload?.finalidade ?? '—')}
                      {kv('Código da Natureza', consultaRow.natureza_cod || '—')}
                      <div style={{ gridColumn: 'span 2' }}>{kv('Descrição / Detalhes da Natureza', consultaRow.natureza_det || '—')}</div>
                    </div>
                    {kv('Chave de Acesso',
                      <span style={{ fontFamily: 'monospace', fontSize: '0.77rem', wordBreak: 'break-all' }}>{consultaRow.chave || '—'}</span>
                    )}
                  </div>

                  {/* ── TOMADOR ── */}
                  {secTitle('Tomador')}
                  <div style={{ padding: '0.7rem 1.25rem' }}>
                    <div style={{ marginBottom: '0.4rem' }}>
                      {kv('Razão Social',
                        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                          {consultaPayload?.clienteSelecionado?.cli_nom || consultaRow.nome_tomador}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem 1.5rem', marginBottom: '0.4rem' }}>
                      {kv('CNPJ / CPF', fmtCnpj(consultaPayload?.clienteSelecionado?.cli_mat || consultaRow.cnpj_tomador))}
                      {kv('Insc. Estadual', consultaPayload?.clienteSelecionado?.cli_ins || '—')}
                      {kv('UF', consultaPayload?.clienteSelecionado?.cli_est || '—')}
                      {kv('Contribuinte', consultaPayload?.clienteSelecionado?.cli_contri ? (consultaPayload.clienteSelecionado.cli_contri === 'N' ? 'Não' : 'Sim') : '—')}
                    </div>
                    {consultaPayload?.endereco && (() => {
                      const end = consultaPayload.endereco
                      return (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0.4rem 1.5rem', marginBottom: '0.4rem' }}>
                            {kv('Logradouro', [end.logradouro, end.numero, end.bairro].filter(Boolean).join(', ') || '—')}
                            {kv('CEP', end.cep || '—')}
                            {kv('Cidade', end.cidade || '—')}
                            {kv('UF', end.uf || '—')}
                          </div>
                          {(end.telefone || end.email) && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.4rem 1.5rem' }}>
                              {end.telefone && kv('Telefone', end.telefone)}
                              {end.email    && kv('E-mail',   end.email)}
                            </div>
                          )}
                        </>
                      )
                    })()}
                  </div>

                  {/* ── VIAGEM ── */}
                  {secTitle('Dados da Viagem')}
                  <div style={{ padding: '0.7rem 1.25rem' }}>
                    {consultaPayload ? (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem 1.5rem', marginBottom: '0.4rem' }}>
                          {kvViagem(consultaPayload.numeroViagem)}
                          {kv('Tipo de Serviço', consultaPayload.tipoServico === '6' ? '6 – Eventual' : consultaPayload.tipoServico === '7' ? '7 – Contínuo' : (consultaPayload.tipoServico ?? '—'))}
                          {kv('Tipo de Viagem', consultaPayload.tipoViagem === '0' ? '0 – Ida e Volta' : consultaPayload.tipoViagem === '1' ? '1 – Somente Ida' : (consultaPayload.tipoViagem ?? '—'))}
                          {kv('Data / Hora', consultaPayload.dhViagem ? consultaPayload.dhViagem.replace('T', ' ').slice(0, 16) : '—')}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem 1.5rem', marginBottom: '0.4rem' }}>
                          {kv('Cód. Veículo', consultaPayload.veiCod || '—')}
                          {kv('Placa', consultaPayload.veiDados?.veipla || consultaPayload.veiPlaca || '—')}
                          {kv('Renavan', consultaPayload.veiDados?.veiren || consultaPayload.veiRenavan || '—')}
                          {kv('UF do Veículo', consultaPayload.veiDados?.veiestado || consultaPayload.veiUf || '—')}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.4rem 1.5rem', alignItems: 'start', marginBottom: '0.4rem' }}>
                          {kv('Descrição do Serviço', consultaPayload.descServico || '—')}
                          {kv('Passageiros', Number(consultaPayload.qtdPassageiros) > 0 ? consultaPayload.qtdPassageiros : '—')}
                        </div>
                        {consultaPayload.estadosViagem && kv('Estados da Viagem', consultaPayload.estadosViagem)}
                      </>
                    ) : (
                      <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Dados não disponíveis para este registro.</span>
                    )}
                  </div>

                  {/* ── ITENS ── */}
                  {secTitle(`Itens / Serviços${consultaPayload?.itens?.length ? ` (${consultaPayload.itens.length})` : ''}`)}
                  <div style={{ padding: '0.7rem 1.25rem' }}>
                    {!consultaPayload?.itens?.length ? (
                      <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Dados não disponíveis para este registro.</span>
                    ) : consultaPayload.itens.map((it: any, idx: number) => {
                      const qty   = Number(it.quantidade)    || 0
                      const unit  = Number(it.valorUnitario) || 0
                      const total = qty * unit
                      const imp = it.impostos ?? {}
                      const ibs = it.ibsCbs   ?? {}
                      const tributos = [
                        { n: 'ICMS',   cst: imp.icmsCst,   base: imp.icmsBase,   aliq: imp.icmsAliq,   valor: imp.icmsDest,   ret: null          },
                        { n: 'PIS',    cst: imp.pisCst,    base: imp.pisBase,    aliq: imp.pisAliq,    valor: imp.pisDest,    ret: imp.pisRet    },
                        { n: 'COFINS', cst: imp.cofinsCst, base: imp.cofinsBase, aliq: imp.cofinsAliq, valor: imp.cofinsDest, ret: imp.cofinsRet },
                        { n: 'ISSQN',  cst: null,          base: imp.issqnBase,  aliq: imp.issqnAliq,  valor: imp.issqnDest,  ret: imp.issqnRet  },
                        { n: 'INSS',   cst: null,          base: imp.inssBase,   aliq: imp.inssAliq,   valor: imp.inssDest,   ret: imp.inssRet   },
                        { n: 'IRRF',   cst: null,          base: imp.irrfBase,   aliq: imp.irrfAliq,   valor: imp.irrfDest,   ret: imp.irrfRet   },
                        { n: 'CSLL',   cst: null,          base: imp.csllBase,   aliq: imp.csllAliq,   valor: imp.csllDest,   ret: imp.csllRet   },
                        { n: 'IPI',    cst: imp.ipiCst,    base: imp.ipiBase,    aliq: imp.ipiAliq,    valor: imp.ipiDest,    ret: null          },
                      ].filter(t => Number(t.valor) !== 0)
                      const ibsLinhas = [
                        { n: 'CBS',                    bc: ibs.cbsBcEf,    aliRef: ibs.cbsAliRef,    red: ibs.cbsRedAli,    aliEf: ibs.cbsAliEf,    valor: ibs.cbsValor    },
                        { n: `IBS UF (${ibs.ibsUfEst || '?'})`, bc: ibs.ibsUfBcEf, aliRef: ibs.ibsUfAliRef, red: ibs.ibsUfRedAli, aliEf: ibs.ibsUfAliEf, valor: ibs.ibsUfValor },
                        { n: 'IBS Municipal',           bc: ibs.ibsMunBcEf, aliRef: ibs.ibsMunAliRef, red: ibs.ibsMunRedAli, aliEf: ibs.ibsMunAliEf, valor: ibs.ibsMunValor },
                      ].filter(l => Number(l.valor) !== 0)
                      return (
                        <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: '0.6rem', overflow: 'hidden' }}>
                          {/* Linha principal do item */}
                          <div style={{
                            display: 'grid', gridTemplateColumns: '1.8fr 0.7fr 1fr 1fr',
                            gap: '0.5rem', padding: '0.5rem 0.75rem',
                            background: '#f9fafb', borderBottom: '1px solid #e5e7eb', alignItems: 'center',
                          }}>
                            <div>
                              <span style={{ fontSize: '0.68rem', color: '#9ca3af', marginRight: 5 }}>#{it.itsCod || (idx + 1)}</span>
                              <span style={{ fontWeight: 700 }}>{it.descricao || '—'}</span>
                              {it.complemento && <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 1 }}>{it.complemento}</div>}
                            </div>
                            <div style={{ fontSize: '0.82rem' }}>
                              <span style={{ fontWeight: 600 }}>{qty}</span>
                              {it.unidade && <span style={{ color: '#9ca3af', marginLeft: 4, fontSize: '0.76rem' }}>{it.unidade}</span>}
                            </div>
                            <div style={{ fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums', color: '#6b7280' }}>
                              R$ {fmtMoeda(unit)}
                            </div>
                            <div style={{ fontSize: '0.92rem', fontWeight: 700, textAlign: 'right', color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
                              R$ {fmtMoeda(total)}
                            </div>
                          </div>
                          {/* Detalhes */}
                          <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.79rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.35rem 1.5rem', marginBottom: '0.5rem' }}>
                              {(it.cidOrigemDes || it.cidOrigemUf)   && kv('Origem',   `${it.cidOrigemDes || ''}${it.cidOrigemUf ? `/${it.cidOrigemUf}` : ''}`)}
                              {(it.cidDestinoDes || it.cidDestinoUf) && kv('Destino',  `${it.cidDestinoDes || ''}${it.cidDestinoUf ? `/${it.cidDestinoUf}` : ''}`)}
                              {it.servPrestCod && kv('Serviço NBS',  `${it.servPrestCod}${it.servPrestDes ? ` – ${it.servPrestDes}` : ''}`)}
                              {it.ibsCbsRegra  && kv('Regra IBS/CBS', `${it.ibsCbsRegra}${it.ibsCbsDes ? ` – ${it.ibsCbsDes}` : ''}`)}
                            </div>
                            {(cstDes(ibs.cst) || classTribDes(ibs.cst, ibs.classTrib)) && (
                              <div style={{ marginBottom: '0.5rem', fontSize: '0.78rem', color: '#374151', lineHeight: 1.4 }}>
                                {cstDes(ibs.cst) && <div><strong>CST {ibs.cst}:</strong> {cstDes(ibs.cst)}</div>}
                                {classTribDes(ibs.cst, ibs.classTrib) && (
                                  <div><strong>cClassTrib {ibs.classTrib}:</strong> {classTribDes(ibs.cst, ibs.classTrib)}</div>
                                )}
                              </div>
                            )}
                            {/* Tabela de impostos tradicionais */}
                            {tributos.length > 0 && (
                              <div style={{ marginBottom: '0.45rem' }}>
                                <div style={{ fontSize: '0.64rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Impostos</div>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                  <thead>
                                    <tr className="thead-claro" style={{ background: '#f9fafb' }}>
                                      {['Tributo','CST','Base de Cálculo','Alíquota','Valor','Retenção'].map(h => (
                                        <th key={h} style={{ padding: '2px 5px', border: '1px solid #e5e7eb', color: '#9ca3af', fontWeight: 600, textAlign: h==='Tributo'||h==='CST' ? 'left' : 'right' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {tributos.map((t, ti) => (
                                      <tr key={ti}>
                                        <td style={{ padding: '2px 5px', border: '1px solid #e5e7eb', fontWeight: 700 }}>{t.n}</td>
                                        <td style={{ padding: '2px 5px', border: '1px solid #e5e7eb', fontFamily: 'monospace' }}>{t.cst ?? '—'}</td>
                                        <td style={{ padding: '2px 5px', border: '1px solid #e5e7eb', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(t.base) ? fmtMoeda(t.base) : '—'}</td>
                                        <td style={{ padding: '2px 5px', border: '1px solid #e5e7eb', textAlign: 'right' }}>{Number(t.aliq) ? `${Number(t.aliq).toFixed(2)}%` : '—'}</td>
                                        <td style={{ padding: '2px 5px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{Number(t.valor) ? fmtMoeda(t.valor) : '—'}</td>
                                        <td style={{ padding: '2px 5px', border: '1px solid #e5e7eb', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#6b7280' }}>{Number(t.ret) ? fmtMoeda(t.ret) : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {/* Tabela IBS/CBS */}
                            {ibsLinhas.length > 0 && (
                              <div>
                                <div style={{ fontSize: '0.64rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>IBS / CBS</div>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                  <thead>
                                    <tr className="thead-claro" style={{ background: '#f0fdf4' }}>
                                      {['Componente','Base Ef.','Alíq. Ref.','Red. (%)','Alíq. Ef.','Valor'].map(h => (
                                        <th key={h} style={{ padding: '2px 5px', border: '1px solid #d1fae5', color: '#9ca3af', fontWeight: 600, textAlign: h==='Componente' ? 'left' : 'right' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ibsLinhas.map((l, li) => (
                                      <tr key={li}>
                                        <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', fontWeight: 700 }}>{l.n}</td>
                                        <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(l.bc) ? fmtMoeda(l.bc) : '—'}</td>
                                        <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right' }}>{Number(l.aliRef) ? `${Number(l.aliRef).toFixed(4)}%` : '—'}</td>
                                        <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right' }}>{Number(l.red) ? `${Number(l.red).toFixed(2)}%` : '—'}</td>
                                        <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right' }}>{Number(l.aliEf) ? `${Number(l.aliEf).toFixed(4)}%` : '—'}</td>
                                        <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: '#15803d' }}>{Number(l.valor) ? fmtMoeda(l.valor) : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* ── TOTAIS ── */}
                  {secTitle('Totais')}
                  <div style={{ padding: '0.7rem 1.25rem', display: 'flex', alignItems: 'flex-start', gap: '2rem' }}>
                    <div>
                      <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>Valor Total da Nota</div>
                      <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
                        R$ {fmtMoeda(consultaRow.valor_total)}
                      </div>
                    </div>
                    {consultaPayload?.itens?.length > 0 && (() => {
                      const itens = consultaPayload.itens
                      const sum = (fn: (it: any) => number) => itens.reduce((a: number, it: any) => a + (fn(it) || 0), 0)
                      const chips = [
                        { l: 'ICMS',    v: sum(it => Number(it.impostos?.icmsDest))   },
                        { l: 'ISSQN',   v: sum(it => Number(it.impostos?.issqnDest))  },
                        { l: 'INSS',    v: sum(it => Number(it.impostos?.inssDest))   },
                        { l: 'IRRF',    v: sum(it => Number(it.impostos?.irrfDest))   },
                        { l: 'PIS',     v: sum(it => Number(it.impostos?.pisDest))    },
                        { l: 'COFINS',  v: sum(it => Number(it.impostos?.cofinsDest)) },
                        { l: 'CSLL',    v: sum(it => Number(it.impostos?.csllDest))   },
                        { l: 'CBS',     v: sum(it => Number(it.ibsCbs?.cbsValor))     },
                        { l: 'IBS UF',  v: sum(it => Number(it.ibsCbs?.ibsUfValor))  },
                        { l: 'IBS Mun', v: sum(it => Number(it.ibsCbs?.ibsMunValor)) },
                      ].filter(c => c.v > 0)
                      if (!chips.length) return null
                      return (
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 5 }}>Tributos Totais</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                            {chips.map(c => (
                              <div key={c.l} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: '3px 10px', fontSize: '0.77rem' }}>
                                <span style={{ color: '#9ca3af', fontWeight: 700, marginRight: 4 }}>{c.l}</span>
                                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>R$ {fmtMoeda(c.v)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })()}
                  </div>

                  {/* ── TRANSMISSÃO ── */}
                  {secTitle('Transmissão SEFAZ')}
                  <div style={{ padding: '0.7rem 1.25rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem 1.5rem', marginBottom: '0.4rem' }}>
                      {kv('Situação', (SITUACAO_LABEL[consultaRow.situacao] ?? { label: consultaRow.situacao }).label)}
                      {kv('cStat', consultaRow.cstat || '—')}
                      {kv('Data de Recebimento', fmtData(consultaRow.dh_recbto))}
                    </div>
                    {consultaRow.xmotivo && <div style={{ marginBottom: '0.4rem' }}>{kv('Motivo', consultaRow.xmotivo)}</div>}
                    {consultaRow.protocolo && kv('Protocolo SEFAZ',
                      <span style={{ fontFamily: 'monospace' }}>{consultaRow.protocolo}</span>
                    )}
                  </div>

                  {/* ── CARTA DE CORREÇÃO ── */}
                  {consultaRow.seq_correcao > 0 && (
                    <>
                      {secTitle('Carta de Correção (CC-e)')}
                      <div style={{ padding: '0.7rem 1.25rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem 1.5rem', marginBottom: '0.75rem' }}>
                          {kv('Sequência', consultaRow.seq_correcao)}
                          {kv('cStat', consultaRow.cstat_correcao || '—')}
                          {kv('Protocolo CC-e',
                            <span style={{ fontFamily: 'monospace' }}>{consultaRow.protocolo_correcao || '—'}</span>
                          )}
                        </div>
                        {consultaPayload?._cce?.correcoes?.length > 0 && (
                          <div>
                            <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' as const, marginBottom: 6 }}>Campos corrigidos</div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                              <thead>
                                <tr style={{ background: '#fef3c7' }}>
                                  {['Grupo', 'Campo', 'Novo valor'].map(h => (
                                    <th key={h} style={{ padding: '4px 8px', border: '1px solid #fde68a', color: '#92400e', fontWeight: 700, textAlign: 'left' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {consultaPayload._cce.correcoes.map((c: any, i: number) => (
                                  <tr key={i}>
                                    <td style={{ padding: '4px 8px', border: '1px solid #fde68a', fontFamily: 'monospace', fontSize: '0.76rem', color: '#6b7280' }}>{c.grupoAlterado || '—'}</td>
                                    <td style={{ padding: '4px 8px', border: '1px solid #fde68a', fontFamily: 'monospace', fontSize: '0.76rem', color: '#6b7280' }}>{c.campoAlterado || '—'}</td>
                                    <td style={{ padding: '4px 8px', border: '1px solid #fde68a' }}>{c.valorAlterado || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* ── FINANCEIRO ── */}
                  {secTitle('Financeiro')}
                  <div style={{ padding: '0.7rem 1.25rem' }}>
                    {consultaFinanceiro.length === 0 ? (
                      <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Nenhum financeiro lançado para esta nota.</span>
                    ) : (
                      <>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#065f46', textTransform: 'uppercase', marginBottom: 6 }}>
                          {consultaFinanceiro.length} parcela{consultaFinanceiro.length > 1 ? 's' : ''}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                          <thead>
                            <tr className="thead-claro" style={{ background: '#f0fdf4' }}>
                              {['Nº', 'Vencimento', 'Valor', 'Saldo', 'Conta'].map(h => (
                                <th key={h} style={{ padding: '4px 8px', border: '1px solid #bbf7d0', color: '#065f46', fontWeight: 700, textAlign: h === 'Nº' ? 'center' : 'left' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {consultaFinanceiro.map((p, i) => (
                              <tr key={i}>
                                <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0', textAlign: 'center' }}>{p.numero}</td>
                                <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0' }}>{fmtData(p.vencimento)}</td>
                                <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoeda(p.valor)}</td>
                                <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoeda(p.saldo)}</td>
                                <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0', fontFamily: 'monospace', fontSize: '0.76rem' }}>{p.contaCod}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </div>

                  {/* ── OBSERVAÇÃO ── */}
                  {consultaPayload?.obs && (
                    <>
                      {secTitle('Observação')}
                      <div style={{ padding: '0.7rem 1.25rem' }}>
                        <div style={{ fontSize: '0.85rem', background: '#f9fafb', borderRadius: 4, padding: '0.5rem 0.75rem', color: '#374151' }}>
                          {consultaPayload.obs}
                        </div>
                      </div>
                    </>
                  )}

                  <div style={{ height: '0.5rem' }} />
                </>
              )}
            </div>

            {/* Rodapé */}
            <div style={{
              borderTop: '1px solid #e5e7eb', padding: '0.6rem 1.25rem',
              display: 'flex', justifyContent: 'flex-end', background: '#f9fafb', flexShrink: 0,
            }}>
              <button className="btn btn-sm" onClick={fecharConsulta} style={{ color: '#6b7280' }}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Financeiro */}
      {finRow && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1000, padding: '24px 12px',
        }} onClick={fecharFinanceiro}>
          <div style={{
            background: '#fff', borderRadius: 8, width: 560, maxWidth: '96vw',
            maxHeight: 'calc(100vh - 48px)', overflow: 'hidden', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>

            {/* Cabeçalho */}
            <div style={{ background: '#065f46', color: '#fff', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>Financeiro</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 2 }}>
                  Nº {finRow.numero} — {finRow.nome_tomador} — R$ {fmtMoeda(finRow.valor_total)}
                </div>
              </div>
              <button onClick={fecharFinanceiro} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '1rem' }}>✕</button>
            </div>

            {/* Corpo */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem' }}>
              {finCarregando ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>Carregando...</div>
              ) : (
                <>
                  {/* Parcelas existentes */}
                  {finExistente.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#065f46', textTransform: 'uppercase', marginBottom: 6 }}>
                        Financeiro existente ({finExistente.length} parcela{finExistente.length > 1 ? 's' : ''})
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                          <tr className="thead-claro" style={{ background: '#f0fdf4' }}>
                            {['Nº', 'Vencimento', 'Valor', 'Saldo', 'Conta'].map(h => (
                              <th key={h} style={{ padding: '4px 8px', border: '1px solid #bbf7d0', color: '#065f46', fontWeight: 700, textAlign: h === 'Nº' ? 'center' : 'left' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {finExistente.map((p, i) => (
                            <tr key={i}>
                              <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0', textAlign: 'center' }}>{p.numero}</td>
                              <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0' }}>{fmtData(p.vencimento)}</td>
                              <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoeda(p.valor)}</td>
                              <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoeda(p.saldo)}</td>
                              <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0', fontFamily: 'monospace', fontSize: '0.76rem' }}>{p.contaCod}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button
                        onClick={deletarFinanceiroLista}
                        disabled={finDeletando}
                        style={{
                          marginTop: '0.5rem', padding: '4px 12px', background: '#fee2e2', color: '#dc2626',
                          border: '1px solid #fca5a5', borderRadius: 4, fontSize: '0.8rem',
                          cursor: finDeletando ? 'not-allowed' : 'pointer', opacity: finDeletando ? 0.6 : 1,
                        }}>
                        {finDeletando ? 'Excluindo...' : 'Excluir financeiro'}
                      </button>
                    </div>
                  )}

                  <hr style={{ borderColor: '#e5e7eb', margin: '0.75rem 0' }} />

                  {/* Criar novo */}
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: 10 }}>
                    {finExistente.length > 0 ? 'Substituir financeiro' : 'Criar financeiro'}
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Qtd. Parcelas</label>
                      <input
                        type="number" min={1} max={36} value={finQtd}
                        onChange={e => { const v = Math.max(1, Number(e.target.value)); setFinQtd(v); recalcularParcelas(v, finIntervalo, finRow) }}
                        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: '0.85rem' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Intervalo (dias)</label>
                      <input
                        type="number" min={1} value={finIntervalo}
                        onChange={e => { const v = Math.max(1, Number(e.target.value)); setFinIntervalo(v); recalcularParcelas(finQtd, v, finRow) }}
                        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: '0.85rem' }}
                      />
                    </div>
                  </div>

                  {/* Conta Financeira */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Conta Financeira</label>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <input
                        type="text" value={finContaCod} placeholder="Código"
                        onChange={e => { setFinContaCod(e.target.value); setFinContaNome('') }}
                        onBlur={() => buscarConta(finContaCod)}
                        style={{ width: 80, border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: '0.85rem' }}
                      />
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input
                          type="text" value={finContaBusca || finContaNome} placeholder="Nome da conta"
                          onChange={async e => {
                            const v = e.target.value; setFinContaBusca(v); setFinContaNome('')
                            if (v.length >= 2) {
                              try {
                                const r = await api.get('/erp/contas', { params: { busca: v } })
                                setFinContaResultados(r.data); setFinContaAberto(r.data.length > 0)
                              } catch { /* */ }
                            } else { setFinContaResultados([]); setFinContaAberto(false) }
                          }}
                          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: '0.85rem' }}
                        />
                        {finContaAberto && finContaResultados.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 180, overflowY: 'auto' }}>
                            {finContaResultados.map(c => (
                              <div key={c.dep_cod} onClick={() => {
                                setFinContaCod(c.dep_cod.trim()); setFinContaNome(c.dep_des)
                                setFinContaBusca(''); setFinContaResultados([]); setFinContaAberto(false)
                              }} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '0.82rem', borderBottom: '1px solid #f3f4f6' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f0fdf4')}
                                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                                <span style={{ fontFamily: 'monospace', color: '#6b7280', marginRight: 8 }}>{c.dep_cod.trim()}</span>{c.dep_des}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tabela de parcelas calculadas */}
                  {finParcelas.length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: 5 }}>Parcelas</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                          <tr className="thead-claro" style={{ background: '#f9fafb' }}>
                            {['Nº', 'Vencimento', 'Valor'].map(h => (
                              <th key={h} style={{ padding: '3px 8px', border: '1px solid #e5e7eb', color: '#9ca3af', fontWeight: 600, textAlign: h === 'Nº' ? 'center' : 'right' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {finParcelas.map((p, i) => (
                            <tr key={i}>
                              <td style={{ padding: '3px 8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>{p.numero}</td>
                              <td style={{ padding: '3px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>{fmtData(p.vencimento)}</td>
                              <td style={{ padding: '3px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoeda(parseFloat(p.valor))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {finParcelas.length === 0 && (
                    <button
                      onClick={() => recalcularParcelas(finQtd, finIntervalo, finRow)}
                      style={{ width: '100%', padding: '7px', background: '#f0fdf4', border: '1px dashed #6ee7b7', borderRadius: 4, fontSize: '0.82rem', color: '#065f46', cursor: 'pointer', fontWeight: 600 }}>
                      Calcular parcelas
                    </button>
                  )}

                  {finErro && (
                    <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#dc2626', background: '#fee2e2', borderRadius: 4, padding: '0.4rem 0.75rem' }}>
                      {finErro}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Rodapé */}
            <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.65rem 1.25rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', background: '#f9fafb' }}>
              <button onClick={fecharFinanceiro} disabled={finSalvando} style={{ padding: '5px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.82rem', cursor: 'pointer', color: '#6b7280' }}>
                Cancelar
              </button>
              <button
                onClick={salvarFinanceiroLista}
                disabled={finSalvando || finParcelas.length === 0}
                style={{
                  padding: '5px 18px', background: '#065f46', color: '#fff', border: 'none',
                  borderRadius: 4, fontSize: '0.82rem', fontWeight: 600,
                  cursor: finSalvando || finParcelas.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: finSalvando || finParcelas.length === 0 ? 0.6 : 1,
                }}>
                {finSalvando ? 'Salvando...' : 'Salvar financeiro'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
